import React, { useEffect, useState, useCallback } from 'react';
import reviewsService from '../services/reviewsService';
import CompanyReviewsStatCard from '../components/CompanyReviewsStatCard';
import CompanyReviewsChart from '../components/CompanyReviewsChart';
import StarRatingCard from '../components/StarRatingCard';
import CompanyEarningsTab from '../components/CompanyEarningsTab';

/**
 * CompanyReviewsPage
 *
 * Displays Reviews metrics for a tracked company.
 *
 * Layout mirrors the screenshot:
 *   - Top bar: company selector (future) + stock ticker widget
 *   - Stat cards row: 28d MA, QTD Reviews, Projected, 7d MA
 *   - Full-width chart: URI time-series with toggle buttons
 */
const CompanyReviewsPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('UPST');
  const [dashboard, setDashboard] = useState(null);
  const [timeSeries, setTimeSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('reviews');
  const [earningsModalOpen, setEarningsModalOpen] = useState(false);
  const [earningsRecordCount, setEarningsRecordCount] = useState(null);

  // Override Layout's .main-content padding/scroll so this page fills edge-to-edge
  useEffect(() => {
    const mc = document.querySelector('.main-content');
    if (mc) {
      mc.style.overflow = 'hidden';
      mc.style.padding = '0';
      mc.style.background = '#0a0900';
    }
    return () => {
      if (mc) {
        mc.style.overflow = '';
        mc.style.padding = '';
        mc.style.background = '';
      }
    };
  }, []);

  // Fetch available companies on mount
  useEffect(() => {
    reviewsService
      .getCompanies()
      .then((data) => {
        setCompanies(data);
        // Default to the first company if 'upstart' not present
        if (data.length && !data.find((c) => c.id === 'upstart')) {
          setSelectedCompanyId(data[0].id);
        }
      })
      .catch(() => {
        // Non-fatal: proceed with default company
      });
  }, []);

  const loadData = useCallback(async (companyId) => {
    setLoading(true);
    setError('');
    try {
      const [dash, series] = await Promise.all([
        reviewsService.getDashboard(companyId),
        reviewsService.getTimeSeries(companyId),
      ]);
      setDashboard(dash);
      setTimeSeries(series.records || []);
    } catch (err) {
      setError('Failed to load company review data. Please try again.');
      console.error('CompanyReviewsPage load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(selectedCompanyId);
  }, [selectedCompanyId, loadData]);

  // --- Derived display values ---

  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);
  const companyName = dashboard?.company_name || selectedCompany?.name || 'Company';
  const ticker = dashboard?.stock_ticker || selectedCompany?.stock_ticker || '';
  const stockPrice = dashboard?.latest_stock_price;
  const dataThrough = dashboard?.data_through;

  const fmt1 = (v) => (v != null ? v.toFixed(1) : null);
  const fmtPct = (v) => (v != null ? `${v > 0 ? '+' : ''}${v.toFixed(1)}%` : null);
  const fmtInt = (v) => (v != null ? v.toLocaleString() : null);

  // Latest star MAs from the most recent time-series record
  const latestRecord = timeSeries.length ? timeSeries[timeSeries.length - 1] : null;
  const starMa28 = latestRecord?.star_ma_28 ?? null;
  const starMa7 = latestRecord?.star_ma_7 ?? null;

  const chartTitle = `URI — ${companyName} Review Index`;

  return (
    <div
      style={{
        backgroundColor: '#0a0900',
        height: 'calc(100vh - 56px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        color: '#fff',
        padding: '8px 16px',
      }}
    >
      {/* ---- Error state ---- */}
      {error && (
        <div className="alert alert-danger" role="alert" style={{ fontSize: '0.85rem' }}>
          {error}
          <button
            className="btn btn-sm btn-outline-danger ms-3"
            onClick={() => loadData(selectedCompanyId)}
          >
            Retry
          </button>
        </div>
      )}

      {/* ---- Loading spinner ---- */}
      {loading && (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
          <div className="spinner-border" style={{ color: '#c9a84c' }} role="status" />
        </div>
      )}

      {/* ---- Main content (shown after load) ---- */}
      {!loading && !error && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

          {/* ---- Tabs ---- */}
          <div
            className="d-flex"
            style={{
              borderBottom: '1px solid #3a2e00',
              marginBottom: 8,
              flexShrink: 0,
              gap: 4,
            }}
          >
            {['reviews', 'earnings'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab ? '2px solid #c9a84c' : '2px solid transparent',
                  color: activeTab === tab ? '#f0c040' : '#7a6a40',
                  fontWeight: activeTab === tab ? 700 : 400,
                  fontSize: '0.8rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '4px 16px 6px',
                  cursor: 'pointer',
                  transition: 'color 0.15s, border-color 0.15s',
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* ---- Top bar: title | stat cards | ticker — all one row ---- */}
          <div className="d-flex align-items-stretch mb-1" style={{ gap: 8, flexShrink: 0 }}>

            {/* Title block */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexShrink: 0 }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                {companyName} Dashboard
              </div>
              <div style={{ fontSize: '0.65rem', color: '#7a6a40', textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
                URI · UPSTARTIQ · FINANCIALS
              </div>
              {companies.length > 1 && (
                <select
                  className="form-select form-select-sm mt-1"
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  style={{ backgroundColor: '#111008', color: '#fff', border: '1px solid #3a2e00', width: 'auto' }}
                >
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Stat cards — flex-grow to fill remaining space (only for reviews tab) */}
            {activeTab === 'reviews' && <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CompanyReviewsStatCard
                  label="28-Day MA (URI)"
                  value={fmt1(dashboard?.ma_28_current)}
                  unit="reviews/day"
                  badge={fmtPct(dashboard?.ma_28_pct_vs_quarter_start)
                    ? `${fmtPct(dashboard?.ma_28_pct_vs_quarter_start)} vs ${dashboard?.quarter_label || 'Q'} start`
                    : null}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CompanyReviewsStatCard
                  label="7-Day MA"
                  value={fmt1(dashboard?.ma_7_current)}
                  unit="reviews/day (short-term)"
                  badge={fmtPct(dashboard?.ma_7_pct_vs_ma_28)
                    ? `${fmtPct(dashboard?.ma_7_pct_vs_ma_28)} vs 28d MA`
                    : null}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CompanyReviewsStatCard
                  label={`QTD Reviews (${dashboard?.quarter_label || ''})`}
                  value={fmtInt(dashboard?.qtd_reviews)}
                  sub={`${dashboard?.qtd_days_tracked ?? 0} days tracked so far`}
                  progress={dashboard?.qtd_progress_pct ?? 0}
                  progressLabel={`${Math.round(dashboard?.qtd_progress_pct ?? 0)}%`}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CompanyReviewsStatCard
                  label={`Projected ${dashboard?.quarter_label || ''}`}
                  value={fmtInt(dashboard?.projected_quarter_total)}
                  unit={dashboard?.qtd_daily_avg != null
                    ? `${fmt1(dashboard.qtd_daily_avg)} reviews/day avg`
                    : undefined}
                  badge={fmtPct(dashboard?.pct_vs_prev_quarter)
                    ? `${fmtPct(dashboard?.pct_vs_prev_quarter)} vs prev quarter`
                    : null}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <StarRatingCard ma28={starMa28} ma7={starMa7} />
              </div>
            </div>}

            {/* Earnings ingest button — only shown on earnings tab */}
            {activeTab === 'earnings' && (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexShrink: 0, marginLeft: 'auto' }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setEarningsModalOpen(true)}
                  style={{
                    backgroundColor: '#c9a84c',
                    border: '1px solid #c9a84c',
                    color: '#0a0900',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <i className="bi bi-upload me-2" />
                  Ingest Earnings Data
                </button>
                {earningsRecordCount !== null && (
                  <div style={{ fontSize: '0.68rem', color: '#7a6a40', marginTop: 4, textAlign: 'center' }}>
                    {earningsRecordCount > 0
                      ? `${earningsRecordCount} record${earningsRecordCount !== 1 ? 's' : ''} ingested`
                      : 'No earnings data yet'}
                  </div>
                )}
              </div>
            )}

            {/* Stock ticker widget */}
            <div
              style={{
                backgroundColor: '#111008',
                border: '1px solid #3a2e00',
                borderRadius: 8,
                padding: '8px 14px',
                textAlign: 'right',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {dataThrough && (
                <div style={{ fontSize: '0.6rem', color: '#7a6a40', marginBottom: 2 }}>
                  Data through {dataThrough}
                </div>
              )}
              <div className="d-flex align-items-center justify-content-end" style={{ gap: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#c9a84c', flexShrink: 0 }} />
                <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem' }}>{ticker}</span>
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0c040', lineHeight: 1.1 }}>
                {stockPrice != null ? `$${stockPrice.toFixed(2)}` : '—'}
              </div>
              <div style={{ fontSize: '0.6rem', color: '#7a6a40', marginTop: 1 }}>Yesterday's Close</div>
            </div>
          </div>

          {/* ---- Tab content ---- */}
          {activeTab === 'reviews' && (
            <CompanyReviewsChart records={timeSeries} title={chartTitle} style={{ flex: 1, minHeight: 0 }} />
          )}
          {activeTab === 'earnings' && (
            <CompanyEarningsTab
              companyName={companyName}
              companyId={selectedCompanyId}
              modalOpen={earningsModalOpen}
              setModalOpen={setEarningsModalOpen}
              onCountChange={setEarningsRecordCount}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyReviewsPage;
